import type { FormulaDependency } from '@signbento/contracts'
import { FORMULA_PROFILE_VERSION } from '@signbento/contracts'

/**
 * Local formula engine — S1.9 / D9.
 * A restricted recursive-descent parser over the adopted operation set. It never
 * evaluates JavaScript, never reads ambient state and never silently coerces: an empty
 * input is unresolved (not zero), a cycle or a division by zero is a blocking error,
 * and every result carries the engine/profile version that produced it.
 *
 * Decimal arithmetic uses the versioned profile in `FORMULA_DECIMAL_POLICY`: values are
 * scaled to a fixed number of decimal places and integer arithmetic is used for
 * add/subtract/multiply, so `0.1 + 0.2` is exactly `0.3` on every platform.
 */

export const FORMULA_ENGINE_VERSION = 1

export const FORMULA_DECIMAL_POLICY = {
  /** Decimal places carried through arithmetic (register §10 freeze: `GATE`). */
  scale: 6,
  /** Rounding policy for ROUND and for the final scale reduction. */
  rounding: 'HALF_UP',
  version: 1,
} as const

export const FORMULA_PROFILE = {
  profile_version: FORMULA_PROFILE_VERSION,
  binary_operators: ['ADD', 'SUBTRACT', 'MULTIPLY', 'DIVIDE'] as const,
  allow_parentheses: true as const,
  allow_decimal_constants: true as const,
  allow_field_references: true as const,
  functions: ['SUM', 'MIN', 'MAX', 'ROUND'] as const,
  decimal_policy_version: FORMULA_DECIMAL_POLICY.version,
  rounding_policy_version: FORMULA_DECIMAL_POLICY.version,
  dependency_rule: {
    allowed_kinds: ['SAME_SIGNER_FIELD', 'SENDER_PREFILLED_IMMUTABLE'] as const,
    cross_signer: 'EXCLUDED' as const,
  },
  empty_input: 'UNRESOLVED' as const,
  on_error: 'REJECT' as const,
  read_only_outputs: true as const,
  evidence: {
    store_expression: true as const,
    store_engine_version: true as const,
    store_dependencies: true as const,
    store_result: true as const,
    recompute_at_authorized_verification: true as const,
  },
}

export type FormulaErrorCode =
  | 'FORMULA_SYNTAX'
  | 'FORMULA_UNKNOWN_FIELD'
  | 'FORMULA_UNKNOWN_FUNCTION'
  | 'FORMULA_TYPE'
  | 'FORMULA_DIVIDE_BY_ZERO'
  | 'FORMULA_CYCLE'
  | 'FORMULA_DEPENDENCY_KIND'
  | 'FORMULA_UNRESOLVED_INPUT'

export interface FormulaIssue {
  readonly code: FormulaErrorCode
  readonly detail?: string
}

export type FormulaEvaluation =
  | {
      readonly kind: 'COMPUTED'
      readonly value: number
      readonly dependencies: readonly FormulaDependency[]
      readonly engine_version: number
    }
  | {
      readonly kind: 'UNRESOLVED'
      readonly reason: 'EMPTY_INPUT'
      readonly dependencies: readonly FormulaDependency[]
    }
  | { readonly kind: 'ERROR'; readonly issue: FormulaIssue }

/* ------------------------------- decimals -------------------------------- */

function scaleFactor(): number {
  return 10 ** FORMULA_DECIMAL_POLICY.scale
}

function toScaled(value: number): number {
  return Math.round(value * scaleFactor())
}

function fromScaled(value: number): number {
  return value / scaleFactor()
}

function roundHalfUp(value: number, decimals: number): number {
  const factor = 10 ** decimals
  const scaled = value * factor
  const rounded = Math.sign(scaled) * Math.floor(Math.abs(scaled) + 0.5)
  return rounded / factor
}

/* -------------------------------- tokens --------------------------------- */

type Token =
  | { readonly kind: 'number'; readonly value: number }
  | { readonly kind: 'identifier'; readonly name: string }
  | { readonly kind: 'operator'; readonly value: '+' | '-' | '*' | '/' }
  | { readonly kind: 'lparen' }
  | { readonly kind: 'rparen' }
  | { readonly kind: 'comma' }

type TokenizeResult =
  | { readonly ok: true; readonly tokens: readonly Token[] }
  | { readonly ok: false; readonly issue: FormulaIssue }

function tokenize(expression: string): TokenizeResult {
  const tokens: Token[] = []
  let index = 0
  while (index < expression.length) {
    const char = expression[index]
    if (char === undefined) break
    if (/\s/.test(char)) {
      index += 1
      continue
    }
    if (char === '(') {
      tokens.push({ kind: 'lparen' })
      index += 1
      continue
    }
    if (char === ')') {
      tokens.push({ kind: 'rparen' })
      index += 1
      continue
    }
    if (char === ',') {
      tokens.push({ kind: 'comma' })
      index += 1
      continue
    }
    if (char === '+' || char === '-' || char === '*' || char === '/') {
      tokens.push({ kind: 'operator', value: char })
      index += 1
      continue
    }
    if (/[0-9.]/.test(char)) {
      const match = /^[0-9]+(\.[0-9]+)?/.exec(expression.slice(index))
      if (match === null) {
        return { ok: false, issue: { code: 'FORMULA_SYNTAX', detail: 'bad number' } }
      }
      tokens.push({ kind: 'number', value: Number(match[0]) })
      index += match[0].length
      continue
    }
    // Braced field references keep ids with hyphens (UUIDs) unambiguous.
    if (char === '{') {
      const match = /^\{([^{}]+)\}/.exec(expression.slice(index))
      if (match === null || match[1] === undefined) {
        return { ok: false, issue: { code: 'FORMULA_SYNTAX', detail: 'unterminated reference' } }
      }
      tokens.push({ kind: 'identifier', name: match[1] })
      index += match[0].length
      continue
    }
    if (/[A-Za-z_]/.test(char)) {
      const match = /^[A-Za-z_][A-Za-z0-9_]*/.exec(expression.slice(index))
      if (match === null) {
        return { ok: false, issue: { code: 'FORMULA_SYNTAX', detail: 'bad identifier' } }
      }
      tokens.push({ kind: 'identifier', name: match[0] })
      index += match[0].length
      continue
    }
    return { ok: false, issue: { code: 'FORMULA_SYNTAX', detail: `unexpected character ${char}` } }
  }
  return { ok: true, tokens }
}

/* -------------------------------- parser --------------------------------- */

interface ParseState {
  readonly tokens: readonly Token[]
  position: number
  readonly resolve: (
    name: string,
  ) =>
    { readonly kind: 'value'; readonly value: number } | { readonly kind: 'empty' } | FormulaIssue
  readonly dependencies: FormulaDependency[]
  issue: FormulaIssue | null
  empty: boolean
}

function fail(state: ParseState, issue: FormulaIssue): number {
  if (state.issue === null) state.issue = issue
  return Number.NaN
}

const FUNCTIONS = new Set(['SUM', 'MIN', 'MAX', 'ROUND'])

function parsePrimary(state: ParseState): number {
  const token = state.tokens[state.position]
  if (token === undefined) return fail(state, { code: 'FORMULA_SYNTAX', detail: 'unexpected end' })
  if (token.kind === 'operator' && (token.value === '-' || token.value === '+')) {
    state.position += 1
    const operand = parsePrimary(state)
    return token.value === '-' ? -operand : operand
  }
  if (token.kind === 'number') {
    state.position += 1
    return token.value
  }
  if (token.kind === 'lparen') {
    state.position += 1
    const value = parseExpression(state)
    const closing = state.tokens[state.position]
    if (closing?.kind !== 'rparen')
      return fail(state, { code: 'FORMULA_SYNTAX', detail: 'missing )' })
    state.position += 1
    return value
  }
  if (token.kind === 'identifier') {
    state.position += 1
    if (FUNCTIONS.has(token.name)) {
      const open = state.tokens[state.position]
      if (open?.kind !== 'lparen')
        return fail(state, { code: 'FORMULA_SYNTAX', detail: 'missing (' })
      state.position += 1
      const args: number[] = []
      if (state.tokens[state.position]?.kind !== 'rparen') {
        args.push(parseExpression(state))
        while (state.tokens[state.position]?.kind === 'comma') {
          state.position += 1
          args.push(parseExpression(state))
        }
      }
      const close = state.tokens[state.position]
      if (close?.kind !== 'rparen')
        return fail(state, { code: 'FORMULA_SYNTAX', detail: 'missing )' })
      state.position += 1
      return applyFunction(state, token.name, args)
    }
    // An identifier followed by "(" can only be a function call.
    if (state.tokens[state.position]?.kind === 'lparen') {
      return fail(state, { code: 'FORMULA_UNKNOWN_FUNCTION', detail: token.name })
    }
    const resolved = state.resolve(token.name)
    if ('code' in resolved) return fail(state, resolved)
    if (resolved.kind === 'empty') {
      state.empty = true
      return Number.NaN
    }
    return resolved.value
  }
  return fail(state, { code: 'FORMULA_SYNTAX', detail: 'unexpected token' })
}

function applyFunction(state: ParseState, name: string, args: readonly number[]): number {
  if (args.length === 0)
    return fail(state, { code: 'FORMULA_SYNTAX', detail: `${name} needs arguments` })
  if (name === 'ROUND') {
    const [value, decimals] = args
    if (value === undefined)
      return fail(state, { code: 'FORMULA_SYNTAX', detail: 'ROUND needs a value' })
    const places = decimals === undefined ? 0 : Math.trunc(decimals)
    return roundHalfUp(value, places)
  }
  if (name === 'SUM')
    return args.reduce((total, value) => fromScaled(toScaled(total) + toScaled(value)), 0)
  if (name === 'MIN') return Math.min(...args)
  if (name === 'MAX') return Math.max(...args)
  return fail(state, { code: 'FORMULA_UNKNOWN_FUNCTION', detail: name })
}

function parseTerm(state: ParseState): number {
  let value = parsePrimary(state)
  for (;;) {
    const token = state.tokens[state.position]
    if (token?.kind !== 'operator' || (token.value !== '*' && token.value !== '/')) return value
    state.position += 1
    const right = parsePrimary(state)
    if (state.issue !== null) return Number.NaN
    if (token.value === '*') {
      // Multiply through the scaled representation so decimals stay exact.
      value = fromScaled(Math.round((toScaled(value) * toScaled(right)) / scaleFactor()))
      continue
    }
    if (right === 0) return fail(state, { code: 'FORMULA_DIVIDE_BY_ZERO' })
    value = fromScaled(Math.round((toScaled(value) * scaleFactor()) / toScaled(right)))
  }
}

function parseExpression(state: ParseState): number {
  let value = parseTerm(state)
  for (;;) {
    const token = state.tokens[state.position]
    if (token?.kind !== 'operator' || (token.value !== '+' && token.value !== '-')) return value
    state.position += 1
    const right = parseTerm(state)
    if (state.issue !== null) return Number.NaN
    value = fromScaled(
      token.value === '+' ? toScaled(value) + toScaled(right) : toScaled(value) - toScaled(right),
    )
  }
}

export interface EvaluateFormulaOptions {
  readonly expression: string
  /** Numeric inputs by field id; a missing entry is an empty input, not zero. */
  readonly inputs: Readonly<Record<string, number | null | undefined>>
  /** Declared dependencies (already validated against the dependency rule). */
  readonly dependencies: readonly FormulaDependency[]
  /** Field ids that make up a cycle (computed by `detectFormulaCycles`). */
  readonly cyclic_field_ids?: readonly string[]
  readonly field_id?: string
}

/** Evaluates one formula. Never throws: every failure is a typed issue. */
export function evaluateFormula(options: EvaluateFormulaOptions): FormulaEvaluation {
  if (
    options.field_id !== undefined &&
    options.cyclic_field_ids !== undefined &&
    options.cyclic_field_ids.includes(options.field_id)
  ) {
    return { kind: 'ERROR', issue: { code: 'FORMULA_CYCLE', detail: options.field_id } }
  }
  for (const dependency of options.dependencies) {
    if (
      dependency.kind !== 'SAME_SIGNER_FIELD' &&
      dependency.kind !== 'SENDER_PREFILLED_IMMUTABLE'
    ) {
      return {
        kind: 'ERROR',
        issue: { code: 'FORMULA_DEPENDENCY_KIND', detail: dependency.kind },
      }
    }
  }

  const tokenized = tokenize(options.expression)
  if (!tokenized.ok) return { kind: 'ERROR', issue: tokenized.issue }
  const tokens = tokenized.tokens
  const state: ParseState = {
    tokens,
    position: 0,
    dependencies: [],
    issue: null,
    empty: false,
    resolve: (name) => {
      if (!(name in options.inputs)) {
        return { code: 'FORMULA_UNKNOWN_FIELD', detail: name }
      }
      const value = options.inputs[name]
      if (value === null || value === undefined) return { kind: 'empty' }
      if (typeof value !== 'number' || Number.isNaN(value)) {
        return { code: 'FORMULA_TYPE', detail: name }
      }
      return { kind: 'value', value }
    },
  }
  const value = parseExpression(state)
  // A recorded issue is the more specific diagnosis; only then look for leftovers.
  if (state.issue !== null) return { kind: 'ERROR', issue: state.issue }
  const trailing = state.tokens[state.position]
  if (trailing !== undefined) {
    return { kind: 'ERROR', issue: { code: 'FORMULA_SYNTAX', detail: 'trailing tokens' } }
  }
  if (state.empty) {
    return { kind: 'UNRESOLVED', reason: 'EMPTY_INPUT', dependencies: options.dependencies }
  }
  if (!Number.isFinite(value)) {
    return { kind: 'ERROR', issue: { code: 'FORMULA_TYPE', detail: 'non-finite result' } }
  }
  return {
    kind: 'COMPUTED',
    value: roundHalfUp(value, FORMULA_DECIMAL_POLICY.scale),
    dependencies: options.dependencies,
    engine_version: FORMULA_ENGINE_VERSION,
  }
}

export interface FormulaNode {
  readonly field_id: string
  readonly dependencies: readonly FormulaDependency[]
}

/**
 * Cycle detection over the declared dependency graph. Returns every field id that
 * participates in a cycle (including self-reference), so callers can block completion
 * instead of producing a fabricated value.
 */
export function detectFormulaCycles(nodes: readonly FormulaNode[]): readonly string[] {
  const byId = new Map(nodes.map((node) => [node.field_id, node]))
  const cyclic = new Set<string>()
  const visiting = new Set<string>()
  const done = new Set<string>()

  function visit(fieldId: string, path: readonly string[]): void {
    if (done.has(fieldId)) return
    if (visiting.has(fieldId)) {
      // Everything on the current path from the first occurrence is part of the cycle.
      const start = path.indexOf(fieldId)
      for (const member of path.slice(start === -1 ? 0 : start)) cyclic.add(member)
      cyclic.add(fieldId)
      return
    }
    visiting.add(fieldId)
    const node = byId.get(fieldId)
    if (node !== undefined) {
      for (const dependency of node.dependencies) {
        if (byId.has(dependency.field_id)) visit(dependency.field_id, [...path, fieldId])
      }
    }
    visiting.delete(fieldId)
    done.add(fieldId)
  }

  for (const node of nodes) visit(node.field_id, [])
  return [...cyclic].sort()
}
